import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(6, 4),
    maxWidth: 680,
    margin: '0 auto',
  },
  heading: {
    marginBottom: theme.spacing(1),
    textAlign: 'center',
  },
  subheading: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
    textAlign: 'center',
  },
  docList: {
    width: '100%',
    marginBottom: theme.spacing(4),
  },
  docItem: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1, 2),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  acked: {
    borderColor: theme.palette.success?.main ?? '#4caf50',
    backgroundColor: (theme.palette.success as any)?.light
      ? `${(theme.palette.success as any).light}22`
      : undefined,
  },
  openLink: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  drawerPaper: {
    width: '50vw',
    minWidth: 360,
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  drawerTitle: {
    flexGrow: 1,
    fontWeight: 600,
  },
  docSection: {
    marginBottom: theme.spacing(3),
  },
  sectionHeading: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  drawerAck: {
    marginTop: 'auto',
    paddingTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

interface DocEntry {
  id: string;
  title: string;
  description: string;
  /** Optional external link — opened in new tab, not in an iframe. */
  externalUrl?: string;
  /** Inline doc sections shown inside the drawer. */
  sections: Array<{ heading: string; body: string }>;
}

const REQUIRED_DOCS: DocEntry[] = [
  {
    id: 'platform-usage-policy',
    title: 'Platform Usage Policy',
    description: 'Rules and guidelines for using the CJOT demo platform.',
    sections: [
      {
        heading: 'Acceptable Use',
        body: 'This platform is provided for Kyndryl internal demos, proof-of-concepts, and developer onboarding. Do not deploy production workloads, store sensitive customer data, or use the platform for personal projects.',
      },
      {
        heading: 'Resource Limits',
        body: 'Keep resource requests reasonable. Namespaces are subject to LimitRanges and ResourceQuotas. Workloads left running for more than 72 hours without activity may be automatically removed to free capacity.',
      },
      {
        heading: 'Shared Infrastructure',
        body: 'AKS, ACR, Key Vault, and PostgreSQL are shared across all teams. Avoid operations that could destabilise the cluster (e.g., draining nodes, deleting cluster-wide resources) without Platform Engineering approval.',
      },
      {
        heading: 'Access Control',
        body: 'Treat your GitHub EMU account credentials as confidential. Do not share access tokens or service account credentials. Report suspected compromise to the Platform Engineering team immediately.',
      },
    ],
  },
  {
    id: 'dev-onboarding',
    title: 'Developer Onboarding Guide',
    description: 'Step-by-step setup for local dev, CI/CD, and catalog registration.',
    sections: [
      {
        heading: 'Local Development',
        body: 'Clone the project repo via Gitea (gitea.kyndemo.live). Use the provided Dev Container or follow the README to install Node.js 20, Go 1.22, or Java 21 depending on your service type. Run `make dev` to start the local stack.',
      },
      {
        heading: 'Registering a Service in the Catalog',
        body: 'Add a catalog-info.yaml to your repository root and register it via the Backstage "Register Existing Component" flow, or use a Software Template that registers it automatically. Every component must declare an owner (group) and a system.',
      },
      {
        heading: 'CI/CD Pipelines',
        body: 'Gitea Actions (GitHub Actions-compatible) runs pipelines on push. Workflows live in .gitea/workflows/. Use the standard build-push-deploy pattern: build image → push to ACR → update Helm values → ArgoCD syncs. See the hello-demo template for a reference workflow.',
      },
      {
        heading: 'Getting Help',
        body: 'Ask in the #platform-engineering channel in Teams, open a GitHub issue against cjot-backstage-az, or reach out to the platform-lead group. The AI Agent Chat (top-right) can also answer platform questions in real time.',
      },
    ],
  },
  {
    id: 'demo-environment-rules',
    title: 'Demo Environment Rules',
    description: 'Expectations for using shared demo infrastructure responsibly.',
    sections: [
      {
        heading: 'Demo Etiquette',
        body: 'Clean up after demos. Delete any workloads, namespaces, or Gitea repositories created solely for a demo within 24 hours of the demo ending. Use the provided cleanup Make targets or ArgoCD app deletion.',
      },
      {
        heading: 'Data in Demos',
        body: 'Use only synthetic or publicly available data. Never use real customer names, PII, or internal confidential data in demo scenarios — even anonymised forms. When in doubt, generate data with Faker or similar tools.',
      },
      {
        heading: 'Gitea Repositories',
        body: 'New repos created through templates are owned by your team\'s Gitea org. Repos are visible to all authenticated users on this instance. Do not commit secrets, credentials, or proprietary code into Gitea.',
      },
      {
        heading: 'Escalation',
        body: 'If you accidentally cause an outage (Backstage down, AKS node pressure, etc.) — don\'t panic. Immediately notify the Platform Engineering team and document what happened. Honest incident reports are always better than silence.',
      },
    ],
  },
];

interface Props {
  docsAcked: string[];
  onAckDoc: (docId: string) => Promise<void>;
  onNext: () => void;
}

export function DocsAckStep({ docsAcked, onAckDoc, onNext }: Props) {
  const classes = useStyles();
  const [openDoc, setOpenDoc] = useState<DocEntry | null>(null);
  const [openedIds, setOpenedIds] = useState<Set<string>>(new Set());
  const [acking, setAcking] = useState<string | null>(null);

  const allAcked = REQUIRED_DOCS.every(d => docsAcked.includes(d.id));

  function handleOpenDoc(doc: DocEntry) {
    setOpenDoc(doc);
    setOpenedIds(prev => new Set([...prev, doc.id]));
  }

  async function handleAck(docId: string) {
    if (docsAcked.includes(docId)) return;
    setAcking(docId);
    try {
      await onAckDoc(docId);
    } finally {
      setAcking(null);
    }
  }

  return (
    <Box className={classes.root}>
      <Typography variant="h5" className={classes.heading}>
        Platform Documentation
      </Typography>
      <Typography variant="body1" className={classes.subheading}>
        Open each document, read it, then check the box to acknowledge. All
        three are required to continue.
      </Typography>

      <List className={classes.docList} disablePadding>
        {REQUIRED_DOCS.map(doc => {
          const acked = docsAcked.includes(doc.id);
          const opened = openedIds.has(doc.id);
          return (
            <ListItem
              key={doc.id}
              className={`${classes.docItem} ${acked ? classes.acked : ''}`}
              onClick={() => handleOpenDoc(doc)}
            >
              <ListItemIcon>
                {acked ? (
                  <CheckCircleIcon style={{ color: '#4caf50' }} />
                ) : (
                  <RadioButtonUncheckedIcon color="disabled" />
                )}
              </ListItemIcon>
              <ListItemText
                primary={doc.title}
                secondary={doc.description}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<MenuBookIcon />}
                className={classes.openLink}
                onClick={e => {
                  e.stopPropagation();
                  handleOpenDoc(doc);
                }}
              >
                {opened ? 'Re-read' : 'Read'}
              </Button>
            </ListItem>
          );
        })}
      </List>

      <Button
        variant="contained"
        color="primary"
        size="large"
        disabled={!allAcked}
        onClick={onNext}
      >
        Next
      </Button>

      {/* Side drawer with inline doc content — no iframes, no recursive Backstage loading */}
      <Drawer
        anchor="right"
        open={openDoc !== null}
        onClose={() => setOpenDoc(null)}
        classes={{ paper: classes.drawerPaper }}
      >
        {openDoc && (
          <>
            <Box className={classes.drawerHeader}>
              <Typography variant="h6" className={classes.drawerTitle}>
                {openDoc.title}
              </Typography>
              {openDoc.externalUrl && (
                <IconButton
                  size="small"
                  href={openDoc.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open in new tab"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )}
              <IconButton size="small" onClick={() => setOpenDoc(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider style={{ marginBottom: 16 }} />

            {/* Inline content sections */}
            <Box flexGrow={1}>
              {openDoc.sections.map(section => (
                <Box key={section.heading} className={classes.docSection}>
                  <Typography variant="subtitle1" className={classes.sectionHeading}>
                    {section.heading}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {section.body}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box className={classes.drawerAck}>
              {acking === openDoc.id ? (
                <CircularProgress size={20} />
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={docsAcked.includes(openDoc.id)}
                      disabled={!openedIds.has(openDoc.id) || docsAcked.includes(openDoc.id)}
                      onChange={() => handleAck(openDoc.id)}
                      color="primary"
                    />
                  }
                  label={`I have read "${openDoc.title}"`}
                />
              )}
              <Button onClick={() => setOpenDoc(null)} variant="outlined" size="small">
                Close
              </Button>
            </Box>
          </>
        )}
      </Drawer>
    </Box>
  );
}
